import { catchError, filter, ignoreElements, map, mergeMap, tap } from "rxjs/operators";
import * as actions from "../store/user.actions";
import { Effect } from "../../../redux/types/effect.type";
import { EffectActionsObservable } from "../../../redux/types/effect-actions-observable.type";
import * as authService from "../../auth/services/auth.service";
import * as uiActions from "../../ui/store/ui.actions";
import { of } from "rxjs";
import * as navigationService from "../../navigation/services/navigation.service";
import { addMessage } from "../../../redux/actions/messagesActions";
import { createError } from "../../../redux/actions/errorHandlingAction";
import { isActionType } from "../../../redux/action.functions";
import { UserActions } from "./user.actions";
import * as userService from "../services/user.service";
import * as authActions from "../../auth/store/auth.actions";

const login: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.Login)),
    map((action) => action.payload),
    map((userData) => actions.validateCredentials(userData))
  );

const validateCredentials: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.ValidateCredentials)),
    map((action) => action.payload),
    map((userData) => ({
      userData,
      errors: authService.validateCredentialInputs(userData.email, userData.password),
    })),
    map(({ userData, errors }) =>
      authService.hasError(errors)
        ? uiActions.setErrors(createError(errors))
        : actions.validateCredentialsSuccess(userData)
    )
  );

const validateCredentialsSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.ValidateCredentialsSuccess)),
    map((action) => action.payload),
    mergeMap((userData) => [uiActions.loadingUi(), actions.fetchAccessToken(userData)])
  );

const fetchUserData: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.FetchUserData)),
    map((action) => action.payload),
    mergeMap((userId) =>
      userService.fetchUser(userId).pipe(
        map((user) => actions.fetchUserDataSuccess(user)),
        catchError((error) => of(uiActions.setErrors(createError(error))))
      )
    )
  );

const fetchUserDataSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.FetchUserDataSuccess)),
    map((action) => action.payload),
    map((user) => actions.setUser(user))
  );

const logout: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.Logout)),
    mergeMap(() => [authActions.destroySession(), actions.reset()])
  );

const fetchAccessToken: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.FetchAccessToken)),
    map((action) => action.payload),
    mergeMap((userData) =>
      authService.login(userData).pipe(
        map((token) => actions.fetchAccessTokenSuccess(token)),
        catchError((error) => of(uiActions.setErrors(createError(error))))
      )
    )
  );

const fetchAccessTokenSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.FetchAccessTokenSuccess)),
    map(() => authService.getCredentials()),
    mergeMap(() => [uiActions.clearErrors(), actions.loginSuccess()]),
    catchError((error) => of(uiActions.setErrors(createError(error))))
  );

const loginSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.LoginSuccess)),
    map((action) => action.payload),
    tap(() => navigationService.goToPostLoginDestination()),
    ignoreElements()
  );

const resetPasswordRequest: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.ResetPasswordRequest)),
    map((action) => action.payload),
    map((userData) => actions.validateResetPasswordRequestCredential(userData))
  );

const validateResetPasswordRequestCredential: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.ValidateResetPasswordRequestCredential)),
    map((action) => action.payload),
    map((userData) => ({
      userData,
      errors: authService.validateEmailCredentialInput(userData.email),
    })),
    map(({ userData, errors }) =>
      authService.hasError(errors)
        ? uiActions.setErrors(createError(errors))
        : actions.validateResetPasswordRequestCredentialSuccess(userData)
    )
  );

const validateResetPasswordRequestCredentialSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.ValidateResetPasswordRequestCredentialSuccess)),
    map((action) => action.payload),
    mergeMap((userData) => [uiActions.loadingUi(), actions.sendResetPasswordRequest(userData)])
  );

const sendResetPasswordRequest: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.SendResetPasswordRequest)),
    map((action) => action.payload),
    mergeMap((userData) =>
      authService.sendResetPasswordRequest(userData).pipe(
        map(() => actions.sendResetPasswordRequestSuccess()),
        catchError((error) => of(uiActions.setErrors(createError(error))))
      )
    )
  );

const sendResetPasswordRequestSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(UserActions.SendResetPasswordRequestSuccess)),
    map((action) => action.payload),
    map(() =>
      addMessage({ text: "Request to reset password successfully sent!", severity: "success" })
    ),
    tap(() => navigationService.goToLogin())
  );

export const UserEffects = [
  login,
  loginSuccess,
  logout,
  fetchAccessToken,
  fetchAccessTokenSuccess,
  validateCredentials,
  validateCredentialsSuccess,
  fetchUserData,
  fetchUserDataSuccess,
  resetPasswordRequest,
  validateResetPasswordRequestCredential,
  validateResetPasswordRequestCredentialSuccess,
  sendResetPasswordRequest,
  sendResetPasswordRequestSuccess,
];
